import React, { useState, useMemo, useEffect, useRef } from 'react'

import { Button, Checkbox, Collapse, Icon } from 'antd'
import { entries } from 'lodash'
import get from 'lodash/get'
import head from 'lodash/head'
import includes from 'lodash/includes'
import keys from 'lodash/keys'
import last from 'lodash/last'
import some from 'lodash/some'
import startCase from 'lodash/startCase'
import PT from 'prop-types'

// import sendFullStoryEvent from '../../../utils/analytics/sendFullStoryEvent'
import { INCLUDE_UNIVERSITY_EMPLOYEES, RANGE_FILTERS } from '../../../utils/applicationConstants'
// import fullstory from '../../../utils/constants/fullstory'
import { getCategorizedFacets, formatFacetKeys } from '../../../utils/data/utils'
import { toTestId } from '../../../utils/testing'
import colors from '../../hocs/colors'

import CheckboxList from './components/CheckboxList'
import RangeFilters from './components/RangeFilters'

const { Panel } = Collapse

const FacetPanel = ({ testId, header, children, ...rest }) => {
  const headerRef = useRef(null)

  useEffect(() => {
    if (!headerRef.current) return
    const item = headerRef.current.closest('.ant-collapse-item')
    if (item) item.setAttribute('data-testid', testId)
  }, [testId])

  return (
    <Panel header={<span ref={headerRef}>{header}</span>} key={rest.key} {...rest}>
      {children}
    </Panel>
  )
}

const CustomFilters = props => {
  const facets = get(props, 'facets')
  const suppliedFilters = get(props, 'suppliedFilters')
  const onChange = get(props, 'onChange')
  const onCloseDrawer = get(props, 'onCloseDrawer')
  const onFiltersApplied = get(props, 'onFiltersApplied')
  // const pageName = get(props, 'pageName')
  const [filters, setFilters] = useState(suppliedFilters)
  const [disabled, setDisabled] = useState(false)

  const onChangeFilters = metric => selectedItems => {
    const newFilters = filters
    newFilters[metric] = selectedItems
    setFilters(newFilters)
  }

  const onChangeIncludeUniversityEmployees = value => {
    const newFilters = filters
    newFilters[INCLUDE_UNIVERSITY_EMPLOYEES] = value
    setFilters(newFilters)
  }

  // Get categorized facets and add include_university_employees to the list
  const categorizedFacets = useMemo(() => {
    const facetKeys = keys(facets)
    facetKeys.push('include_university_employees')
    const categorized = getCategorizedFacets(facetKeys)
    return categorized
  }, [facets])

  // Renders checkbox options for facet metricKeys
  const renderCheckboxOptions = metricKey => {
    if (includes(INCLUDE_UNIVERSITY_EMPLOYEES, metricKey)) {
      return (
        <Checkbox
          defaultChecked={get(suppliedFilters, INCLUDE_UNIVERSITY_EMPLOYEES, false)}
          onChange={val => onChangeIncludeUniversityEmployees(get(val, 'target.checked'))}
          style={{ paddingBottom: '8px', paddingTop: '8px' }}
          aria-label="Include University Employees"
        >
          <span aria-hidden="true">Yes</span>
        </Checkbox>
      )
    }
    return (
      <CheckboxList
        metric={metricKey}
        onChange={onChangeFilters(metricKey)}
        options={get(facets, metricKey, [])}
        selectedFilters={
          get(suppliedFilters, metricKey, []) ? get(suppliedFilters, metricKey, []) : []
        }
        disabled={disabled}
        setDisabled={setDisabled}
      />
    )
  }

  const onSubmitFilters = () => {
    // if (filters) {
    //   const {
    //     ages = [],
    //     cities = [],
    //     colleges = [],
    //     current_employers = [],
    //     current_job_categories = [],
    //     degree_levels = [],
    //     genders = [],
    //     grad_years = [],
    //     highest_degree_level_anywhere = [],
    //     include_university_employees = false,
    //     incomes = [],
    //     industries = [],
    //     majors = [],
    //     military_status = [],
    //     states = [],
    //     universities = [],
    //   } = filters
    //   // const eventProperties = {
    //   //   ages_ints: ages,
    //   //   cities_strs: cities,
    //   //   colleges_strs: colleges,
    //   //   current_employers_strs: current_employers,
    //   //   current_job_categories_strs: current_job_categories,
    //   //   degree_levels_strs: degree_levels,
    //   //   genders_strs: genders,
    //   //   grad_years_ints: grad_years,
    //   //   highest_degree_level_anywhere_strs: highest_degree_level_anywhere,
    //   //   include_university_employees_bool: include_university_employees,
    //   //   incomes_ints: incomes,
    //   //   industries_strs: industries,
    //   //   majors_strs: majors,
    //   //   military_status_strs: military_status,
    //   //   states_strs: states,
    //   //   universities_strs: universities,
    //   // }
    //   // const fsEventName =
    //   //   pageName === 'graduate-explorer'
    //   //     ? fullstory.eventNames.graduateExplorerFilters
    //   //     : pageName === 'graduate-outcomes'
    //   //     ? fullstory.eventNames.graduateOutcomesFilters
    //   //     : fullstory.eventNames.addTopLevelFilters

    //   // sendFullStoryEvent(fsEventName, eventProperties)
    // }

    // Determine if a filter was removed compared to the previously supplied filters.
    // A filter is considered removed when at least one key had selections before and
    // now has fewer (or none). Otherwise treat it as a filter being applied.
    const filterRemoved = some(keys(suppliedFilters), key => {
      const prev = get(suppliedFilters, key)
      const next = get(filters, key)
      if (prev === true && next !== true) return true
      if (Array.isArray(prev) && prev.length > 0) {
        if (!Array.isArray(next) || next.length < prev.length) return true
      }
      return false
    })

    onChange(filters)
    if (onFiltersApplied) {
      onFiltersApplied(filterRemoved ? 'Filter removed.' : 'Filter applied.')
    }
    onCloseDrawer()
  }

  return (
    <>
      <div
        className="custom-reports-filters-container"
        style={{
          height: 'calc(100% - 98px)',
          margin: '0 auto',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          position: 'fixed',
          top: '48px',
          width: '300px',
        }}
      >
        <Collapse
          bordered={false}
          defaultActiveKey={['Education', 'Employment', 'Demographic', 'Geographic', 'Other']}
          expandIcon={({ isActive }) => (
            <Icon type="up" rotate={isActive ? 0 : 180} aria-hidden="true" />
          )}
          expandIconPosition="right"
          style={{
            background: '#E6E6E6',
            borderBottom: '1px solid #D4D4D4',
            borderColor: 'ECECEC',
            borderRadius: '0',
            color: '#222222',
            fontFamily: 'Arial',
            fontSize: 14,
            fontWeight: 'bold',
            lineHeight: '24',
            padding: '0px',
          }}
        >
          {entries(categorizedFacets).map(([categoryName, facetKeys]) => {
            // Skip empty categories
            if (facetKeys.length === 0) return null

            return (
              <Panel header={categoryName} key={categoryName}>
                <Collapse
                  bordered={false}
                  expandIcon={({ isActive }) => (
                    <Icon type="up" rotate={isActive ? 0 : 180} aria-hidden="true" />
                  )}
                  expandIconPosition="right"
                >
                  {facetKeys.map(metricKey => {
                    const matchingFilter = get(suppliedFilters, metricKey)
                    const lengthDescription =
                      matchingFilter && matchingFilter === true
                        ? ' (selected)'
                        : matchingFilter && matchingFilter.length > 0
                        ? includes(RANGE_FILTERS, metricKey)
                          ? `  (${head(matchingFilter)}-${last(matchingFilter)} selected)`
                          : `  (${matchingFilter.length} selected)`
                        : ''
                    const formattedMetricKey = formatFacetKeys(metricKey)

                    return (
                      <FacetPanel
                        testId={`${toTestId(formattedMetricKey)}-facet`}
                        header={`${startCase(formattedMetricKey)}${lengthDescription}`}
                        key={`${categoryName}-${metricKey}`}
                        style={{ background: '#FFFFFF', fontWeight: 'normal', padding: '0' }}
                      >
                        {includes(RANGE_FILTERS, metricKey) ? (
                          <RangeFilters
                            metric={metricKey}
                            onChange={onChangeFilters(metricKey)}
                            options={get(facets, metricKey, [])}
                            selectedFilters={
                              get(suppliedFilters, metricKey, [])
                                ? get(suppliedFilters, metricKey, [])
                                : []
                            }
                          />
                        ) : (
                          renderCheckboxOptions(metricKey)
                        )}
                      </FacetPanel>
                    )
                  })}
                </Collapse>
              </Panel>
            )
          })}
        </Collapse>
      </div>
      <div
        style={{
          background: '#fff',
          bottom: 0,
          marginTop: 20,
          padding: '10px 16px',
          position: 'fixed',
          right: 0,
          textAlign: 'right',
          width: '300px',
          zIndex: 1000,
        }}
      >
        <Button
          style={{
            marginRight: 8,
          }}
          onClick={onCloseDrawer}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmitFilters}
          type="primary"
          disabled={disabled}
          style={{ backgroundColor: colors.blue3, outline: colors.blue3 }}
        >
          Submit
        </Button>
      </div>
    </>
  )
}

CustomFilters.propTypes = {
  facets: PT.object,
  onChange: PT.func.isRequired,
  onCloseDrawer: PT.func,
  onFiltersApplied: PT.func,
  pageName: PT.string,
  suppliedFilters: PT.object,
}

CustomFilters.defaultProps = {
  facets: [],
}

export default CustomFilters
